/** What a run cost, and the files and documents it produced - shared by the project
 * hub and the agent pages. The transcript, live panels and staged changes live in
 * their own modules beside this one. */
import type { ReactNode } from "react";

import {
  useDeleteRunArtifact,
  useRunArtifactDownloadUrl,
  type RunArtifact,
  type RunDriveDocument,
} from "../api/hooks/useStatefulAgents";
import { useProjectsList } from "../api/hooks/useProjectsList";
import { agentDisplayLabel } from "../utils/runParticipants";
import { projectDisplayName } from "../utils/projectName";
import { formatUsd } from "../utils/estimateRunCost";
import { isDemoMode } from "../demo/demoMode";
import { DriveDocumentsPanel } from "./DriveDocumentsPanel";
import { GeneratedFilesPanel } from "./GeneratedFilesPanel";
import styles from "./RunView.module.css";

/** What a run cost, priced server-side and rendered the same way everywhere.
 *
 * `usd` is a number, `null` (no ledger row - a dash, never a free run) or
 * `undefined` (payload carries no spend). The demo-mode check lives here so every
 * caller hides spend alike. `prefix` renders only alongside a shown figure.
 */
export const RunSpendText = ({
  usd,
  prefix,
}: {
  usd: number | null | undefined;
  prefix?: ReactNode;
}) => {
  if (usd === undefined || isDemoMode()) return null;
  return (
    <>
      {prefix}
      {usd === null ? (
        <span className={styles.muted} title="No AI spend was recorded for this run">
          -
        </span>
      ) : (
        <span>{formatUsd(usd)}</span>
      )}
    </>
  );
};


// The run's generated files, bound to the run's own download route. A thin
// wrapper so both run surfaces mount one component rather than repeating the
// hook + name lookup; the projects query is app-wide and already cached.
export const RunGeneratedFiles = ({
  runId,
  artifacts,
}: {
  runId: string;
  artifacts: RunArtifact[];
}) => {
  const download = useRunArtifactDownloadUrl(runId);
  const remove = useDeleteRunArtifact(runId);
  const projects = useProjectsList();
  return (
    <GeneratedFilesPanel
      files={artifacts}
      projectLabel={(projectId) => {
        const p = projects.data?.find((proj) => proj.project_id === projectId);
        return p ? projectDisplayName(p) : projectId;
      }}
      onRequestUrl={async (artifactId) =>
        (await download.mutateAsync({ artifact_id: artifactId })).url
      }
      onDelete={async (artifactId) => {
        await remove.mutateAsync({ artifact_id: artifactId });
      }}
    />
  );
};

// The Drive documents in this run's scope. No hook of its own: they ride the
// run's full read, so there is nothing to fetch and nothing to poll.
export const RunDriveDocuments = ({ documents }: { documents: RunDriveDocument[] }) => (
  <DriveDocumentsPanel documents={documents} agentLabel={agentDisplayLabel} />
);

